import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isPartnersOpen, setIsPartnersOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const servicesLinks = [
    { name: 'Loans', path: '/loans' },
    { name: 'Portfolio Management', path: '/portfolio-management' },
    { name: 'Insurance', path: '/insurance' }
  ];

  const partnersLinks = [
    { name: 'Lending Partners', path: '/lending-partners' },
    { name: 'Partner Onboarding', path: '/partner-onboarding' }
  ];

  const mainLinks = [
    { name: 'Calculators', path: '/calculators' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_direct-agents/artifacts/48hd3h7g_ChatGPT%20Image%20Jan%2017%2C%202026%2C%2001_40_25%20PM%20%281%29.png"
              alt="Capital IQ Logo"
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-base font-medium transition-colors duration-200 ${
                location.pathname === '/'
                  ? 'text-[#DC143C]'
                  : 'text-gray-700 hover:text-[#DC143C]'
              }`}
            >
              Home
            </Link>
            
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                className={`flex items-center gap-1 text-base font-medium transition-colors duration-200 ${
                  servicesLinks.some(link => location.pathname === link.path)
                    ? 'text-[#DC143C]'
                    : 'text-gray-700 hover:text-[#DC143C]'
                }`}
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isServicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                  {servicesLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`block px-4 py-3 text-base font-medium transition-colors duration-200 ${
                        location.pathname === link.path
                          ? 'bg-red-50 text-[#DC143C]'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-[#DC143C]'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Partners Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsPartnersOpen(true)}
              onMouseLeave={() => setIsPartnersOpen(false)}
            >
              <button
                className={`flex items-center gap-1 text-base font-medium transition-colors duration-200 ${
                  partnersLinks.some(link => location.pathname === link.path)
                    ? 'text-[#DC143C]'
                    : 'text-gray-700 hover:text-[#DC143C]'
                }`}
              >
                Partners
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isPartnersOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isPartnersOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                  {partnersLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`block px-4 py-3 text-base font-medium transition-colors duration-200 ${
                        location.pathname === link.path
                          ? 'bg-red-50 text-[#DC143C]'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-[#DC143C]'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {mainLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-[#DC143C]'
                    : 'text-gray-700 hover:text-[#DC143C]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700 hover:text-[#DC143C] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-base font-medium transition-colors text-left py-2 ${
                  location.pathname === '/'
                    ? 'text-[#DC143C]'
                    : 'text-gray-700 hover:text-[#DC143C]'
                }`}
              >
                Home
              </Link>
              
              {/* Services Mobile */}
              <div>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="flex items-center justify-between w-full text-base font-medium text-gray-700 hover:text-[#DC143C] py-2"
                >
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isServicesOpen && (
                  <div className="ml-4 mt-2 space-y-2">
                    {servicesLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block text-base font-medium py-2 ${
                          location.pathname === link.path
                            ? 'text-[#DC143C]'
                            : 'text-gray-600 hover:text-[#DC143C]'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Partners Mobile */}
              <div>
                <button
                  onClick={() => setIsPartnersOpen(!isPartnersOpen)}
                  className="flex items-center justify-between w-full text-base font-medium text-gray-700 hover:text-[#DC143C] py-2"
                >
                  Partners
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isPartnersOpen ? 'rotate-180' : ''}`} />
                </button>
                {isPartnersOpen && (
                  <div className="ml-4 mt-2 space-y-2">
                    {partnersLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block text-base font-medium py-2 ${
                          location.pathname === link.path
                            ? 'text-[#DC143C]'
                            : 'text-gray-600 hover:text-[#DC143C]'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {mainLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-base font-medium transition-colors text-left py-2 ${
                    location.pathname === link.path
                      ? 'text-[#DC143C]'
                      : 'text-gray-700 hover:text-[#DC143C]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;